import { SearchOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ButtonIcon from 'components/ButtonIcon';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import AuthContext from 'context/AuthContext';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { exportAllProjectsCSV, getAllProjectsForAdmin } from 'services/project-service';
import { formatClimate, formatType } from 'utils/Formatter';

interface ProjectData {
  id: string;
  name: string;
  type: string;
  climate: string;
  city: string;
  address: string;
  createdBy: string;
  updatedAt: string;
  organization: {
    _id: string;
    company_name: string;
    company_email: string;
    image_url?: string;
  };
  totalRequiredPointsWithTransaction: number;
  revisions: any[];
}

const AdminProjects = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId, setOrganizationId } = useContext(AuthContext);
  
  // State for pagination and filtering
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('projectId');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [searchError, setSearchError] = useState<string>('');

  // State for menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);
  const open = Boolean(anchorEl);

  // Query for projects data
  const { data: projectsData, isLoading, refetch } = useQuery(
    ['GetAllProjectsForAdmin', page, pageSize, sortOrder, sortBy, searchValue, searchType],
    () => getAllProjectsForAdmin(organizationId, {
      page,
      pageSize,
      sortOrder,
      sortBy,
      search: searchValue,
      searchType
    }) 
   );

  const projects = projectsData?.data || [];
  const totalCount = projectsData?.paging?.total || 0;

  // React Query mutation for CSV export
  const exportCSVMutation = useMutation(async () => {
    return await exportAllProjectsCSV();
  });

  const handleDownloadCSV = async () => {
    try {
      const blob = await exportCSVMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'admin-projects.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Optionally show error
    }
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };


  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Handle search with debounce
  const debouncedSearch = debounce((value: string, searchType: string) => {
    if (value.trim() === '') {
      setSearchValue('');
      setSearchType('');
      setPage(1);
      return;
    }
    setSearchValue(value);
    setSearchType(searchType);
    setPage(1);
  }, 3000);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // Clear error when user starts typing
    setSearchError('');
    
    // Validate projectId format
    if (searchType === 'projectId' && value.trim() !== '') {
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!objectIdRegex.test(value)) {
        setSearchError('Invalid project ID format. Must be 24 hexadecimal characters.');
        return;
      }
    }
    
    // Immediate update for UI responsiveness
    if (value.trim() === '') {
      setSearchValue('');
      setSearchType('');
      setPage(1);
    } else {
      setSearchValue(value);
      setSearchType(searchType);
      setPage(1);
    }
    
    // Debounced call for final search
    debouncedSearch(value, searchType);
  };

  const handleSearchTypeChange = (event: any) => {
    setSearchType(event.target.value);
    setPage(1);
  };

  // Handle sorting
  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newOrderBy);
    setPage(1);
  };

  // Handle menu
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, project: ProjectData) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  // Navigate to project details
  const handleViewProject = (project: ProjectData) => {

    localStorage.setItem('organizationId', project.organization._id);
    setOrganizationId(project.organization._id);
    navigate(`/admin/business/${project.organization._id}/project/${project.id}/board`);
    handleMenuClose();
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yy HH:mm');
    } catch {
      return dateString;
    }
  };


  
  return (
    <Box>
      {/* Search and Filter Controls - New Design */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: "144px",
          mb: "22px",
          flexWrap: "wrap",
          gap: '1rem'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem',
            flexWrap: "wrap",
            flex: 1
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '24px',
                lineHeight: '31px',
                color: '#2F3B49',
              }}
            >
              {t('PROJECTS_TITLE')}
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            width: { xs: '100%', md: '50%', lg: '50%' }
          }}>
            <TextField
              type='text'
              fullWidth
              variant='outlined'
              placeholder={t('PROJECTS_SEARCH_PLACEHOLDER')}
              sx={{
                width: { md: '100%', lg: '590px' },
                '& .MuiInputBase-root': {
                  WebkitFontSmoothing: 'auto',
                  padding: "0px",
                },
                '& .MuiInputBase-inputAdornedStart': {
                  padding: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FormControl>
                      <Select
                        value={searchType}
                        onChange={handleSearchTypeChange}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              '& .MuiMenuItem-root': {
                                fontSize: '16px',
                              },
                            },
                          },
                        }}
                        sx={{
                          height: '50px',
                          width: '109px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '4px 0px 0px 4px',
                          borderRight: '1px solid #C4C4C4',
                          '& .MuiSelect-select': {
                            padding: '8px 10px 8px 8px',
                            fontSize: '16px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="projectId">{t('ADMIN_PROJECTS_PROJECT_ID')}</MenuItem>
                        <MenuItem value="projectName">{t('ADMIN_PROJECTS_PROJECT_NAME')}</MenuItem>
                        <MenuItem value="projectClientName">{t('PROJECTS_SEARCH_BY_CLIENT_NAME')}</MenuItem>
                        <MenuItem value="companyName">{t('ADMIN_PROJECTS_FILTER_COMPANY_NAME')}</MenuItem>
                        <MenuItem value="projectType">{t('ADMIN_PROJECTS_PROJECT_TYPE')}</MenuItem>
                        <MenuItem value="projectClimate">{t('ADMIN_PROJECTS_PROJECT_CLIMATE')}</MenuItem>
                        <MenuItem value="projectVersion">{t('ADMIN_PROJECTS_PROJECT_VERSION')}</MenuItem>
                      </Select>
                    </FormControl>
                    {searchValue ? (
                      <ButtonIcon onClick={() => {
                        setSearchValue('');
                        setPage(1);
                      }}>
                        <ClearIcon />
                      </ButtonIcon>
                    ) : (
                      <ButtonIcon disabled>
                        <SearchOutlined />
                      </ButtonIcon>
                    )}
                  </InputAdornment>
                ),
                sx: { height: '50px' }
              }}
              value={searchValue}
              onChange={handleSearchChange}
              autoComplete='off'
            />
          </Box>
        </Box>
        {/* CSV Export Button */}
        <ButtonIcon
          onClick={handleDownloadCSV}
          disabled={exportCSVMutation.isLoading}
        >
          {exportCSVMutation.isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <FileDownloadOutlinedIcon fontSize='large' />
          )}
        </ButtonIcon>
      </Box>

      {/* Projects Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
            <CustomTableFontCell
                sortDirection={sortBy === "name" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "name")}
                >
                  {t('ADMIN_PROJECTS_PROJECT_LIST_NAME')}
                  {sortBy === "name" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
            
             
              <CustomTableFontCell
                sortDirection={sortBy === "type" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "type"}
                  direction={sortBy === "type" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "type")}
                >
                  {t('ADMIN_PROJECTS_PROJECT_TYPE')}
                  {sortBy === "type" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "climate" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "climate"}
                  direction={sortBy === "climate" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "climate")}
                >
                  {t('ADMIN_PROJECTS_PROJECT_CLIMATE')}
                  {sortBy === "climate" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "city" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "city"}
                  direction={sortBy === "city" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "city")}
                >
                  {t('ADMIN_PROJECTS_PROJECT_CITY')}
                  {sortBy === "city" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "company_name" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "company_name"}
                  direction={sortBy === "company_name" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "company_name")}
                >
                  {t('ADMIN_PROJECTS_COMPANY_NAME')}
                  {sortBy === "company_name" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "revisions" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "revisions"}
                  direction={sortBy === "revisions" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "revisions")}
                >
                  {t('ADMIN_PROJECTS_REVISIONS')}
                  {sortBy === "revisions" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "totalRequiredPointsWithTransaction" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "totalRequiredPointsWithTransaction"}
                  direction={sortBy === "totalRequiredPointsWithTransaction" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "totalRequiredPointsWithTransaction")}
                >
                  {t('ADMIN_PROJECTS_CREDITS_USED')}
                  {sortBy === "totalRequiredPointsWithTransaction" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "updatedAt" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "updatedAt")}
                >
                  {t('ADMIN_PROJECTS_LAST_UPDATE')}
                  {sortBy === "updatedAt" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell align="right"></CustomTableFontCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t('ADMIN_PROJECTS_NO_PROJECTS_FOUND')}
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project: ProjectData) => (
                <TableRow
                  key={project.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: 'pointer',
                  }}
                >
                  
                  <CustomTableCell style={{ width: "200px" }}>
                  
                      {project.name}
                 
                  
                  </CustomTableCell>
                  <CustomTableCell style={{ width: "150px" }}>
                    <span className="data">
                      {t(formatType(project.type))}
                    </span>
                  </CustomTableCell>
                  <CustomTableCell style={{ width: "120px" }}>
                    <span className="data">
                      {t(formatClimate(project.climate))}
                    </span>
                  </CustomTableCell>
                  <CustomTableCell style={{ width: "120px" }}>
                    <span className="data">
                      {project.city}
                    </span>
                  </CustomTableCell>
                  <CustomTableCell style={{ width: "250px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                      }}
                    >
                      {project.organization?.image_url && (
                        <img src={project.organization.image_url} width="30px" alt="Company logo" />
                      )}
                          {project.organization?.company_name || 'N/A'}
                        
                 
                    </Box>
                  </CustomTableCell>
                  <CustomTableCell style={{ width: "100px" }}>
                    <span className="data">
                      {project.revisions?.length || 0}
                    </span>
                  </CustomTableCell>
                  <CustomTableCell style={{ width: "120px" }}>
                    <span className="data">
                      {project.totalRequiredPointsWithTransaction || 0}
                    </span>
                  </CustomTableCell>
                  <CustomTableCell width={"100px"}>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <span className="data">
                        {formatDate(project.updatedAt)}
                      </span>
                    </div>
                  </CustomTableCell>
                  <CustomTableCell width={"100px"} align="right">
                    <ButtonIcon
                    isActive={selectedProject?.id === project.id ? true : false}
                      onClick={(e) => handleMenuClick(e, project)}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <MoreHorizIcon />
                    </ButtonIcon>
                    <Menu
                      sx={{ fontSize: '14px !important' }}
                      anchorEl={anchorEl}
                      open={open && selectedProject?.id === project.id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => handleViewProject(project)}>
                        {t('ADMIN_PROJECTS_VIEW_PROJECT')}
                      </MenuItem>
                    </Menu>
                  </CustomTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        rowsPerPageOptions={[20]}
        rowsPerPage={pageSize}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) =>
          t('MUI_PAGINATION_RANGE', {
            from: from ,
            to: Math.min(to, count),
            count
          })}
      />
    </Box>
  );
};

export default AdminProjects;
